import { z } from "zod";
import { PropertyCardSchema } from "./listings";

/**
 * The signed-in visitor's own data: /v1/me and everything under it.
 *
 * Every reply here is private to one account. None of it is cached, and the
 * gateway refuses all of it without a verified login.
 */

const iso = z.iso.datetime();

export const LandlordProfileSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  displayName: z.string().nullable(),
  bio: z.string().nullable(),
  companyName: z.string().nullable(),
  verifiedLandlord: z.boolean(),
  reviewStatus: z.enum(["NONE", "PENDING", "VERIFIED", "REJECTED"]),
  reviewRequestedAt: iso.nullable(),
  reviewedAt: iso.nullable(),
  reviewedById: z.uuid().nullable(),
  reviewNotes: z.string().nullable(),
  propertyLimit: z.number().int(),
  createdAt: iso,
  updatedAt: iso,
});

export const TenantProfileSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  gender: z.enum(["MALE", "FEMALE", "NONBINARY", "PREFER_NOT"]).nullable(),
  dateOfBirth: iso.nullable(),
  budgetMin: z.number().int().nullable(),
  budgetMax: z.number().int().nullable(),
  moveInDate: iso.nullable(),
  leaseLengthPref: z.string().nullable(),
  bio: z.string().nullable(),
  lifestyle: z.unknown().nullable(),
  occupation: z.enum(["STUDENT", "WORKING", "OTHER"]).nullable(),
  schoolId: z.uuid().nullable(),
  isLooking: z.boolean(),
  verificationStatus: z.enum(["NONE", "PENDING", "VERIFIED", "REJECTED"]),
  createdAt: iso,
  updatedAt: iso,
});
export type TenantProfileDto = z.infer<typeof TenantProfileSchema>;

/** The account itself, with both profiles. What requireUser() resolves to. */
export const CurrentUserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  fullName: z.string().nullable(),
  phone: z.string().nullable(),
  phoneVerifiedAt: iso.nullable(),
  avatarUrl: z.string().nullable(),
  activeRole: z.enum(["LANDLORD", "TENANT"]),
  status: z.enum(["ACTIVE", "SUSPENDED"]),
  isAdmin: z.boolean(),
  emailVerifiedAt: iso.nullable(),
  createdAt: iso,
  updatedAt: iso,
  lastActiveAt: iso.nullable(),
  ratingAvg: z.number().nullable(),
  ratingCount: z.number().int(),
  landlordProfile: LandlordProfileSchema.nullable(),
  tenantProfile: TenantProfileSchema.nullable(),
});
export type CurrentUserDto = z.infer<typeof CurrentUserSchema>;

export const NotificationTypeSchema = z.enum(["MESSAGE", "BOOKING", "SAVED_SEARCH"]);

export const NotificationSchema = z.object({
  id: z.uuid(),
  type: NotificationTypeSchema,
  title: z.string(),
  body: z.string().nullable(),
  url: z.string().nullable(),
  readAt: iso.nullable(),
  createdAt: iso,
});
export const NotificationsQuerySchema = z.object({ take: z.coerce.number().int().min(1).max(50).default(50) });
export const NotificationsSchema = z.object({ items: z.array(NotificationSchema) });

/** What the site header needs on every page: who you are and your latest alerts. */
export const HeaderSchema = z.object({
  activeRole: z.enum(["LANDLORD", "TENANT"]),
  isAdmin: z.boolean(),
  fullName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  notifications: z.array(NotificationSchema),
});
export type HeaderDto = z.infer<typeof HeaderSchema>;

/** The dashboard's stat row, for both roles, in one reply. */
export const DashboardStatsSchema = z.object({
  unreadNotifications: z.number().int(),
  favorites: z.number().int(),
  landlord: z.object({ properties: z.number().int(), pendingRequests: z.number().int(), bedsOpen: z.number().int() }),
  tenant: z.object({ activeRequests: z.number().int() }),
});
export type DashboardStats = z.infer<typeof DashboardStatsSchema>;

export const FavoritesSchema = z.object({ items: z.array(PropertyCardSchema) });

export const SavedSearchSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  /** The stored filters, exactly as saved. The website parses them with its own filter keys. */
  filters: z.unknown(),
  alertsEnabled: z.boolean(),
  frequency: z.enum(["INSTANT", "DAILY", "WEEKLY"]),
});
export const SavedSearchesSchema = z.object({ items: z.array(SavedSearchSchema) });
export type SavedSearchDto = z.infer<typeof SavedSearchSchema>;

export const RoommatePreferenceSchema = z.object({
  shareForMatching: z.boolean(),
  sleepSchedule: z.string().nullable(),
  cleanliness: z.string().nullable(),
  noise: z.string().nullable(),
  smoking: z.string().nullable(),
  guests: z.string().nullable(),
  studyHabit: z.string().nullable(),
});
export const RoommatePreferenceReplySchema = z.object({ preference: RoommatePreferenceSchema.nullable() });
export type RoommatePreferenceDto = z.infer<typeof RoommatePreferenceSchema>;

export const TenantProfileReplySchema = z.object({ profile: TenantProfileSchema.nullable() });

/** One of the visitor's own booking requests or availability inquiries. */
export const MyBookingSchema = z.object({
  id: z.uuid(),
  status: z.enum(["REQUESTED", "APPROVED", "REJECTED", "CANCELLED", "EXPIRED", "COMPLETED"]),
  isInquiry: z.boolean(),
  tenantId: z.uuid(),
  propertyId: z.uuid(),
  requestedAt: iso,
  respondedAt: iso.nullable(),
  moveInDate: iso.nullable(),
  /** The all-in price agreed when the request was made, if it was recorded. */
  advertisedPriceCents: z.number().int().nullable(),
  property: z.object({ id: z.uuid(), title: z.string(), slug: z.string(), city: z.string() }),
  room: z.object({ label: z.string(), pricePerSlot: z.number().int(), advertisedPriceCents: z.number().int() }),
  /** Has the visitor already reviewed this stay? */
  reviewed: z.boolean(),
  /** Has a "this place is as advertised" confirmation been filed for this booking? */
  confirmed: z.boolean(),
});
export const MyBookingsSchema = z.object({ items: z.array(MyBookingSchema) });
export type MyBookingDto = z.infer<typeof MyBookingSchema>;
